import React, {
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import classNames from "classnames";
import _ from "lodash";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";

// design system
import { DSProvider, Dialog, IconButton } from "@toddle-edu/ds-web";
import { CaretClosedFilled } from "@toddle-edu/ds-icons";

// components
import Header from "./components/Header";
import Body from "./components/Body";
import Footer from "./components/Footer";
import ModalitiesViewer from "./components/ModalitiesViewer";

// modules
import { deleteAttachment } from "modules/Services";
import { getAIConversationAttachmentsQuery } from "./modules/AIChatQueries";

// context
import { AIChatContext } from "./context/AIChatContext";

// utils
import { getAttachmentId, isModalityAttachmentType } from "./modules/Utils";

// constants
import {
  DIALOG_ACTIONS,
  DIALOGS_INFO,
  INITIAL_DIALOG_STATE,
  MODALITY_TYPE_INFO_MAPPING,
} from "./modules/Constants";

// scss
import classes from "./AIChat.scss";

const styles = { rotateBy90: { transform: "rotate(90deg)" } };

const BREAKPOINT_SCREEN_WIDTH = 1200;

const AIChat = forwardRef((props, ref) => {
  const {
    mode,
    source,
    children,
    conversation,
    entityType,
    entitySubType,
    innerContainerStyle,
    onModalityStateChange,
    showCollapsedModality,
    onToggleModalityContainer = _.noop,
    shouldDisplayModalities = false,
    showChatContainerFullWidth = true,
    contextProps = {},
    currentAIState,
    setCurrentAIState,
  } = props;

  // ========= state =========
  const [value, setValue] = useState("");
  const [isAIGeneratingResponse, setIsAIGeneratingResponse] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isScrollingToBottom, setIsScrollingToBottom] = useState(true);
  const [shouldRenderEmptySpace, setShouldRenderEmptySpace] = useState(true);
  const [selectedModalityId, setSelectedModalityId] = useState(null);
  const [dialogState, setDialogState] = useState(INITIAL_DIALOG_STATE);
  const [modalityWidth, setModalityWidth] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isPendingAttachmentUpdate, setIsPendingAttachmentUpdate] = useState(
    false
  );
  const [isScreenSmall, setIsScreenSmall] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < BREAKPOINT_SCREEN_WIDTH
      : false
  );
  const [editMessageId, setEditMessageId] = useState(null);

  const { t } = useI18n();
  const dispatch = useDispatch();

  // ========= ref =========
  const containerRef = useRef(null);
  const resizeStartXRef = useRef(null);
  const initialModalityWidthRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const workbookCompRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const updateCallsQueueRef = useRef([]);
  const modalitiesViewerFunctionsRef = useRef({});

  useImperativeHandle(
    ref,
    () => ({ setSelectedModalityId, videoPlayerRef }),
    []
  );

  // Extract attachments from all messages
  const messages = [..._.get(conversation, "messages.edges", [])].reverse();
  const messageAttachments = _.chain(messages)
    .flatMap(messageEdge => {
      const messageNode = _.get(messageEdge, "node");
      const attachments = _.get(messageNode, "attachments", []);
      const authorType = _.get(messageNode, "authorType");

      // Add authorType to each attachment from this message
      return _.map(attachments, attachment => ({
        ...attachment,
        authorType,
      }));
    })
    .filter(attachment => !_.isNil(attachment))
    .value();

  // Combine message attachments with local attachments and remove duplicates
  const attachments = _.chain([...messageAttachments, ...localAttachments])
    .uniqBy("id")
    .value();

  // ========= handlers =========
  const onMediaClick = ({ attachment }) => {
    setSelectedModalityId(attachment.id);
  };

  const resetCloseDialogState = useCallback(() => {
    setDialogState(INITIAL_DIALOG_STATE);
  }, []);

  const closeModalityView = useCallback(
    (id = null) => {
      resetCloseDialogState();
      onMediaClick({ attachment: { id } });
    },
    [resetCloseDialogState, onMediaClick]
  );

  const onClose = () => {
    closeModalityView();
  };

  // Cleanup and close modality (removes local attachments and deletes if needed)
  const discardChangesAndClose = useCallback(async () => {
    const deletedAttachment = _.get(dialogState, "params.deletedAttachment");

    const id = deletedAttachment?.id;

    if (id) {
      setLocalAttachments(prevAttachments => {
        return _.filter(
          prevAttachments,
          prevAttachment => prevAttachment?.id !== id
        );
      });

      const attachmentId = getAttachmentId(deletedAttachment);
      const refetchQueries = [];

      const isConversationAttachment = _.some(conversation?.attachments, {
        id: attachmentId,
      });

      // Refetch conversation attachments if the deleted attachment is part of the conversation to update the cache
      if (isConversationAttachment && conversation?.id) {
        refetchQueries.push({
          query: getAIConversationAttachmentsQuery,
          variables: { id: conversation.id },
        });
      }

      dispatch(deleteAttachment({ attachmentId, refetchQueries }));

      // If modality attachment is deleted, set the last modality as active
      if (isModalityAttachmentType(deletedAttachment) && attachments) {
        const lastModality = _.findLast(
          attachments,
          attachment =>
            isModalityAttachmentType(attachment) && attachment?.id !== id
        );

        if (lastModality?.id) {
          closeModalityView(lastModality.id);
        } else {
          closeModalityView();
        }
      } else {
        closeModalityView();
      }
    }
  }, [dispatch, closeModalityView, dialogState, attachments, conversation]);

  const onLocalAttachmentsChange = useCallback(
    (updatedAttachmentsOrUpdater, deletedAttachment) => {
      // Handle updater function pattern
      if (_.isFunction(updatedAttachmentsOrUpdater)) {
        setLocalAttachments(updatedAttachmentsOrUpdater);
        return;
      }

      // Check if a warning dialog should be shown for deleted attachment
      const { id } = deletedAttachment || {};
      const showWarningDialog =
        !_.isEmpty(id) && isModalityAttachmentType(deletedAttachment);

      if (showWarningDialog) {
        setDialogState({
          action: DIALOG_ACTIONS.SHOW_CLOSE_DIALOG,
          params: { deletedAttachment },
        });
      } else {
        setLocalAttachments(updatedAttachmentsOrUpdater);
      }
    },
    []
  );

  // ========= resize handlers =========
  const handleResizeStart = useCallback(
    e => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartXRef.current = e.clientX;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        initialModalityWidthRef.current =
          modalityWidth || containerRect.width * 0.7;
      }
    },
    [modalityWidth]
  );

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaX = resizeStartXRef.current - e.clientX;
      const newModalityWidth = initialModalityWidthRef.current + deltaX;

      const MIN_MODALITY_WIDTH = 720;
      const MIN_CHAT_WIDTH = 440;
      const maxModalityWidth = containerRect.width - MIN_CHAT_WIDTH;

      const constrainedWidth = Math.min(
        Math.max(newModalityWidth, MIN_MODALITY_WIDTH),
        maxModalityWidth
      );

      setModalityWidth(constrainedWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    resizeStartXRef.current = null;
    initialModalityWidthRef.current = null;
  }, []);

  // reset resize styling when switching to view mode
  useEffect(() => {
    if (mode !== "view") return;

    setModalityWidth(null);
    handleResizeEnd();
  }, [mode, handleResizeEnd]);

  // ========= resize effect =========
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // ========= context value =========
  const contextValue = {
    mode,
    value,
    setValue,
    entityType,
    entitySubType,
    localAttachments,
    conversation,
    videoPlayerRef,
    currentAIState,
    setCurrentAIState,
    messageAttachments,
    setLocalAttachments: onLocalAttachmentsChange,
    lastUserMessageRef,
    workbookCompRef,
    showScrollToBottom,
    setShowScrollToBottom,
    shouldScrollToBottom,
    setShouldScrollToBottom,
    isScrollingToBottom,
    setIsScrollingToBottom,
    shouldRenderEmptySpace,
    setShouldRenderEmptySpace,
    onMediaClick,
    selectedModalityId,
    setSelectedModalityId,
    isPendingAttachmentUpdate,
    setIsPendingAttachmentUpdate,
    isAIGeneratingResponse,
    setIsAIGeneratingResponse,
    updateCallsQueueRef,
    modalitiesViewerFunctionsRef,
    ...contextProps,
    editMessageId,
    setEditMessageId,
  };

  // ========= computed values =========
  const hasSelectedModality = !_.isNil(selectedModalityId);

  // ========= effects =========
  useEffect(() => {
    if (onModalityStateChange) {
      onModalityStateChange(hasSelectedModality);
    }
  }, [hasSelectedModality, onModalityStateChange]);

  // Handle window resize to track screen size
  useEffect(() => {
    const handleResize = _.debounce(() => {
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
    }, 300);

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const renderCollapsedModality = () => {
    const attachmentOptions = _(attachments).compact().uniqBy("id").value();
    const selectedAttachmentData = _.find(attachmentOptions, {
      id: selectedModalityId,
    });
    const attachmentType = _.get(selectedAttachmentData, "type");

    return (
      <div className={classes.collapsedHeaderContainer}>
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          size="small"
          icon={<CaretClosedFilled style={styles.rotateBy90} />}
          onClick={onToggleModalityContainer}
        />
        <div className={classNames("text-body", classes.labelContainer)}>
          {t(MODALITY_TYPE_INFO_MAPPING[attachmentType]?.label)}
        </div>
      </div>
    );
  };

  const renderModality = () => {
    const isCollapsed = showCollapsedModality && isScreenSmall;
    const modalityStyle =
      modalityWidth && showChatContainerFullWidth && !isCollapsed
        ? { width: `${modalityWidth}px`, flex: "0 0 auto" }
        : {};

    const modalityContainerClass = classNames(modalitiesClass, {
      [classes.modalitiesCollapsed]: isCollapsed,
    });

    return (
      <>
        {isCollapsed ? renderCollapsedModality() : null}
        <div
          className={modalityContainerClass}
          style={modalityStyle}
          id="modality-viewer-container"
        >
          {showChatContainerFullWidth && mode !== "view" && !isCollapsed ? (
            <button
              type="button"
              className={classes.resizeHandle}
              onMouseDown={handleResizeStart}
              aria-label="Resize modality"
              tabIndex={0}
            />
          ) : null}
          <ModalitiesViewer
            source={source}
            attachments={attachments}
            onClose={onClose}
            isResizing={isResizing}
            onToggleModalityContainer={onToggleModalityContainer}
            assessmentId={contextProps?.assessmentId}
            courseId={contextProps?.courseId}
            gradeId={contextProps?.gradeId}
          />
        </div>
      </>
    );
  };

  const containerClass = classNames(classes.container, {
    [classes.containerWithModalities]: shouldDisplayModalities,
    [classes.containerWithVisibleModality]: hasSelectedModality,
  });

  const innerContainerClass = classNames(classes.innerContainer, {
    [classes.innerContainerWithModality]:
      shouldDisplayModalities && hasSelectedModality && !showCollapsedModality,
    [classes.innerContainerWithoutModality]:
      shouldDisplayModalities && !hasSelectedModality,
  });

  const modalitiesClass = classNames(classes.modalitiesContainer, {
    [classes.modalitiesVisible]: hasSelectedModality,
    [classes.modalitiesHidden]: !hasSelectedModality,
    [classes.modalitiesContainerWithMargin]: entitySubType === "ai_tutor",
  });

  // ========= dialog config =========
  const { action: dialogAction, params } = dialogState;
  const dialogInfo = DIALOGS_INFO[dialogAction];
  const modalityLabel =
    _.get(params, "deletedAttachment.name") || t("common:untitled");

  const innerContainerStyleWithWidth = useMemo(
    () =>
      modalityWidth && hasSelectedModality && showChatContainerFullWidth
        ? { ...innerContainerStyle, flex: "1 1 auto", minWidth: "400px" }
        : innerContainerStyle,
    [
      modalityWidth,
      hasSelectedModality,
      innerContainerStyle,
      showChatContainerFullWidth,
    ]
  );

  return (
    <DSProvider dsVersion="2.0">
      <div className={containerClass} ref={containerRef}>
        <AIChatContext.Provider value={contextValue}>
          <div
            className={innerContainerClass}
            style={innerContainerStyleWithWidth}
          >
            {children}

            {showChatContainerFullWidth &&
            mode === "edit" &&
            hasSelectedModality ? (
              <button
                tabIndex={0}
                type="button"
                aria-label="Resize modality"
                onMouseDown={handleResizeStart}
                className={classes.chatResizeHandle}
              />
            ) : null}
          </div>

          {shouldDisplayModalities && hasSelectedModality
            ? renderModality()
            : null}
        </AIChatContext.Provider>

        {/* Warning modal  */}
        {!_.isNil(dialogAction) && dialogInfo ? (
          <Dialog
            onClose={resetCloseDialogState}
            title={dialogInfo.title({ t, label: modalityLabel })}
            message={dialogInfo.message({ t, label: modalityLabel })}
            buttonOneText={dialogInfo.buttonOneText({ t })}
            buttonTwoText={dialogInfo.buttonTwoText({ t })}
            onButtonOneClick={resetCloseDialogState}
            onButtonTwoClick={discardChangesAndClose}
          />
        ) : null}
      </div>
    </DSProvider>
  );
});

AIChat.displayName = "AIChat";

AIChat.Header = Header;
AIChat.Body = Body;
AIChat.Footer = Footer;

export default AIChat;
